import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';

import { FaPlus, FaBuilding, FaHome, FaShoppingCart, FaFilter } from 'react-icons/fa';
import './HomeDashboard.css';
import StatCard from './Home/StatCard';
import ProjectAnalysisChart from './Home/ProjectAnalysisChart';
import ExpertCallingCard from './Home/ExpertCallingCard';
import ContactCard from './Home/ContactCard';


const getRecentItems = (items, limit = 4) => {
  if (!items.length) return [];
  return [...items]
    .sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA || (b.id || 0) - (a.id || 0);
    })
    .slice(0, limit);
};

function Home() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    allProjects: [],
    activeProjects: [],
    endedProjects: [],
    commercialProjects: [],
    residentialProjects: [],
    expertData: [],
    contactData: [],
    packageData: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const headers = { headers: { Authorization: `Bearer ${token}` } };

      const [projectsRes, expertRes, contactRes] = await Promise.allSettled([
        getRequest('projects', headers),
        getRequest('expertCalling', headers),
        getRequest('contactus', headers),
      ]);

      const safeData = (result) => result.status === 'fulfilled' ? result.value?.data?.data || [] : [];

      const allProjects = safeData(projectsRes);
      const expertData = safeData(expertRes);
      const contactData = safeData(contactRes);

      const activeProjects = allProjects.filter((p) => p.is_active === true || p.is_active === 1);
      const commercialProjects = activeProjects.filter((p) => p.project_type === 'Commercial');
      const residentialProjects = activeProjects.filter((p) => p.project_type === 'Residential');

      setDashboardData({
        allProjects,
        activeProjects,
        commercialProjects,
        residentialProjects,
        expertData,
        contactData,
      });
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="dash-loading__spinner" />
        <p>Loading Dashboard...</p>
      </div>
    );
  }

  const {
    allProjects,
    activeProjects,
    commercialProjects,
    residentialProjects,
    expertData,
    contactData,
  } = dashboardData;

  const recentExpertCalls = getRecentItems(expertData, 4);
  const recentContactUs = getRecentItems(contactData, 4);

  const quickActions = [
    { label: 'Add Project', icon: FaPlus, path: '/projects/add' },
    { label: 'View Packages', icon: FaShoppingCart, path: '/packages' },
  ];

  return (
    <div className="dash-root">
      <div className="dash-welcome">
        <div className="dash-welcome__text">
          <h1>Welcome, Admin!</h1>
          <p>Here&apos;s your latest dashboard metrics and activities.</p>
        </div>
        <div className="dash-welcome__actions">
          <button className="btn-primary" onClick={() => navigate('/projects/add')}>
            <FaPlus /> Add New Project
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard 
          label="Total Projects" 
          value={allProjects.length} 
          type="primary" 
          change="+12%" 
          subtext="Overall count" 
          onClick={() => navigate('/projects')}
        />
        <StatCard 
          label="Active Projects" 
          value={activeProjects.length} 
          type="secondary" 
          change="+5%" 
          subtext="Currently running" 
          onClick={() => navigate('/projects?status=active')}
        />
        <StatCard 
          label="Total Contact" 
          value={contactData.length} 
          type="info" 
          change="+18%" 
          subtext="Contact form submissions" 
          onClick={() => navigate('/contactus')}
        />
        <StatCard 
          label="Total Enquiry" 
          value={expertData.length} 
          type="warning" 
          subtext="Expert calling enquiries" 
          onClick={() => navigate('/expertcalling')}
        />
      </div>

      <div className="main-grid">
        <div className="charts-col">
          <div className="chart-container">
            <div className="chart-header">
              <h3>Project Growth & Analytics</h3>
              <button className="btn-secondary">
                <FaFilter /> Weekly
              </button>
            </div>
            <ProjectAnalysisChart projects={activeProjects} />
          </div>

          <div className="activity-card">
            <div className="activity-card__header">
              <h3>Expert Enquiries</h3>
              <button className="btn-secondary" onClick={() => navigate('/expertcalling')}>View All</button>
            </div>
            <div className="activity-list" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
              {recentExpertCalls.map((e, i) => <ExpertCallingCard key={i} expert={e} />)}
              {recentExpertCalls.length === 0 && <p className="text-muted small">No expert calls.</p>}
            </div>
          </div>
        </div>

        <div className="sidebar-col">
          <div className="activity-card">
            <div className="activity-card__header">
              <h3>Quick Actions</h3>
            </div>
            <div className="quick-actions">
              {quickActions.map((action, index) => (
                <button key={index} className="action-btn" onClick={() => navigate(action.path)}>
                  <div className="action-icon"><action.icon /></div>
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="activity-card">
            <div className="activity-card__header">
              <h3>Project Categories</h3>
            </div>
            <div className="activity-list">
              <div 
                className="activity-item" 
                onClick={() => navigate('/projects?type=Commercial')} 
                style={{cursor:'pointer', background: '#F9FAFB'}}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/projects?type=Commercial')}
              >
                <div className="activity-avatar" style={{background:'#F97316'}}><FaBuilding /></div>
                <div className="activity-content">
                  <div className="activity-title">Commercial</div>
                  <div className="activity-subtitle">{commercialProjects.length} Projects</div>
                </div>
              </div>
              <div 
                className="activity-item" 
                onClick={() => navigate('/projects?type=Residential')} 
                style={{cursor:'pointer', background: '#F9FAFB'}}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/projects?type=Residential')}
              >
                <div className="activity-avatar" style={{background:'#F97316'}}><FaHome /></div>
                <div className="activity-content">
                  <div className="activity-title">Residential</div>
                  <div className="activity-subtitle">{residentialProjects.length} Projects</div>
                </div>
              </div>
            </div>
          </div>

          <div className="activity-card">
            <div className="activity-card__header">
              <h3>Latest Enquiries</h3>
              <button className="btn-secondary" onClick={() => navigate('/contactus')}>View All</button>
            </div>
            <div className="activity-list">
              {recentContactUs.map((c, i) => <ContactCard key={i} contact={c} />)}
              {recentContactUs.length === 0 && <p className="text-muted small">No enquiries found.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;